import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supportAPI } from '../lib/api';

const SupportSessionContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// STUN servers (should match backend)
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

export const SupportSessionProvider = ({ children }) => {
  const [pendingSession, setPendingSession] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected, failed
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cursorEnabled, setCursorEnabled] = useState(false);
  
  // Refs for WebRTC and WebSocket
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const cursorCallbackRef = useRef(null);
  
  // Check for pending support sessions (called periodically)
  const checkPendingSession = useCallback(async () => {
    // Only check if there's a tenant access token (not admin)
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return;
    }
    
    try {
      const response = await supportAPI.getPendingSession();
      if (response.data.has_pending && response.data.session) {
        setPendingSession(response.data.session);
      } else {
        setPendingSession(null);
      }
    } catch (err) {
      // Silently fail - not critical (includes 403 for non-tenant users)
    }
  }, []);
  
  // Poll for pending sessions every 10 seconds
  useEffect(() => {
    // Initial check
    checkPendingSession();
    
    // Set up polling - but check if polling is paused
    pollIntervalRef.current = setInterval(() => {
      // P0 FIX: Skip poll if paused (modal open or critical operation)
      const { isPollingPaused } = require('../lib/api');
      if (!isPollingPaused()) {
        checkPendingSession();
      }
    }, 10000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [checkPendingSession]);
  
  // Clean up WebRTC and WebSocket connections
  const cleanup = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsSharing(false);
    setConnectionStatus('disconnected');
  }, []);
  
  // Accept a support session
  const acceptSession = useCallback(async (sessionId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await supportAPI.acceptSession(sessionId);
      const { tenant_join_token } = response.data;
      
      setActiveSession({
        ...pendingSession,
        token: tenant_join_token,
      });
      setPendingSession(null);
      
      return { success: true, token: tenant_join_token };
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to accept session';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [pendingSession]);
  
  // Decline a support session
  const declineSession = useCallback(async (sessionId) => {
    setLoading(true);
    setError(null);
    
    try {
      await supportAPI.declineSession(sessionId);
      setPendingSession(null);
      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to decline session';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Start screen sharing (called after accepting)
  const startSharing = useCallback(async () => {
    if (!activeSession?.id || !activeSession?.token) {
      setError('No active session');
      return { success: false, error: 'No active session' };
    }
    
    setConnectionStatus('connecting');
    setError(null);
    
    try {
      // Request screen capture - browser tab only by default
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser', // Prefer browser tab
          selfBrowserSurface: 'include', // Allow sharing current tab
        },
        audio: false, // No audio for privacy
        preferCurrentTab: true, // Prefer current tab if available
      });
      
      streamRef.current = stream;
      
      // Handle user stopping share via browser UI
      stream.getVideoTracks()[0].onended = () => {
        stopSharing();
      };
      
      // Connect to WebSocket for signaling
      const wsUrl = `${WS_URL}/api/ws/support/${activeSession.id}?token=${activeSession.token}&role=tenant`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('WebSocket connected for signaling');
      };
      
      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'config':
            // Received ICE servers config, initialize peer connection
            initializePeerConnection(data.ice_servers || ICE_SERVERS);
            break;
            
          case 'peer_joined':
            if (data.role === 'admin') {
              // Admin connected, create and send offer
              await createAndSendOffer();
            }
            break;
            
          case 'answer':
            // Received answer from admin
            if (pcRef.current && data.sdp) {
              await pcRef.current.setRemoteDescription(new RTCSessionDescription({
                type: 'answer',
                sdp: data.sdp
              }));
            }
            break;
            
          case 'ice_candidate':
            // Received ICE candidate from admin
            if (pcRef.current && data.candidate) {
              await pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
            }
            break;
            
          case 'peer_left':
            if (data.role === 'admin') {
              // Admin disconnected
              setConnectionStatus('disconnected');
            }
            break;
            
          case 'cursor_position':
            // Admin is sending cursor position - emit to listeners
            if (cursorCallbackRef.current) {
              cursorCallbackRef.current({
                x: data.x,
                y: data.y,
                visible: data.visible
              });
            }
            break;
            
          case 'error':
            setError(data.message);
            break;
            
          default:
            break;
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('Connection error');
        setConnectionStatus('failed');
      };
      
      ws.onclose = () => {
        console.log('WebSocket closed');
        if (isSharing) {
          setConnectionStatus('disconnected');
        }
      };
      
      setIsSharing(true);
      return { success: true };
      
    } catch (err) {
      console.error('Failed to start sharing:', err);
      
      // Handle specific errors
      let errorMsg = 'Failed to start screen sharing';
      if (err.name === 'NotAllowedError') {
        errorMsg = 'Screen sharing was cancelled or denied';
      } else if (err.name === 'NotFoundError') {
        errorMsg = 'No screen available to share';
      } else if (err.name === 'NotSupportedError') {
        errorMsg = 'Screen sharing is not supported in this browser';
      }
      
      setError(errorMsg);
      setConnectionStatus('failed');
      cleanup();
      return { success: false, error: errorMsg };
    }
  }, [activeSession, isSharing, cleanup]);
  
  // Initialize WebRTC peer connection
  const initializePeerConnection = useCallback((iceServers) => {
    const pc = new RTCPeerConnection({
      iceServers: iceServers
    });
    pcRef.current = pc;
    
    // Add the screen stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, streamRef.current);
      });
    }
    
    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          candidate: event.candidate.toJSON()
        }));
      }
    };
    
    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
      switch (pc.connectionState) {
        case 'connected':
          setConnectionStatus('connected');
          // Notify backend that sharing started
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'sharing_started' }));
          }
          break;
        case 'disconnected':
        case 'closed':
          setConnectionStatus('disconnected');
          break;
        case 'failed':
          setConnectionStatus('failed');
          setError('Connection failed. This may be due to network restrictions (firewall/NAT).');
          // Notify backend of failure
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ 
              type: 'connection_failed',
              reason: 'WebRTC connection failed - possible NAT/firewall issues'
            }));
          }
          break;
      }
    };
    
    // Handle ICE connection state
    pc.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'failed') {
        setError('Connection failed. Try again or use a different network.');
        setConnectionStatus('failed');
      }
    };
    
    // Create data channel for future cursor overlay (no UI yet)
    const dataChannel = pc.createDataChannel('annotations', {
      ordered: true
    });
    dataChannel.onopen = () => console.log('Data channel open');
    dataChannel.onclose = () => console.log('Data channel closed');
    
  }, []);
  
  // Create and send WebRTC offer
  const createAndSendOffer = useCallback(async () => {
    if (!pcRef.current || !wsRef.current) return;
    
    try {
      const offer = await pcRef.current.createOffer();
      await pcRef.current.setLocalDescription(offer);
      
      wsRef.current.send(JSON.stringify({
        type: 'offer',
        sdp: offer.sdp
      }));
    } catch (err) {
      console.error('Failed to create offer:', err);
      setError('Failed to establish connection');
    }
  }, []);
  
  // Stop sharing
  const stopSharing = useCallback(async () => {
    // Notify server
    if (activeSession?.id) {
      try {
        await supportAPI.stopSharing(activeSession.id);
      } catch (err) {
        console.error('Failed to notify server of stop:', err);
      }
    }
    
    // Notify via WebSocket
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'sharing_stopped' }));
    }
    
    cleanup();
    setActiveSession(null);
    setCursorEnabled(false);
  }, [activeSession, cleanup]);
  
  // Retry connection
  const retryConnection = useCallback(async () => {
    cleanup();
    setError(null);
    await startSharing();
  }, [cleanup, startSharing]);
  
  // Enable cursor overlay (tenant action)
  const enableCursor = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    
    setCursorEnabled(true);
    wsRef.current.send(JSON.stringify({ type: 'cursor_enabled' }));
  }, []);
  
  // Disable cursor overlay (tenant action)
  const disableCursor = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    
    setCursorEnabled(false);
    wsRef.current.send(JSON.stringify({ type: 'cursor_disabled' }));
  }, []);
  
  // Toggle cursor
  const toggleCursor = useCallback(() => {
    if (cursorEnabled) {
      disableCursor();
    } else {
      enableCursor();
    }
  }, [cursorEnabled, enableCursor, disableCursor]);
  
  // Register cursor position callback
  const onCursorPosition = useCallback((callback) => {
    cursorCallbackRef.current = callback;
    return () => {
      cursorCallbackRef.current = null;
    };
  }, []);
  
  const value = {
    pendingSession,
    activeSession,
    isSharing,
    connectionStatus,
    error,
    loading,
    cursorEnabled,
    checkPendingSession,
    acceptSession,
    declineSession,
    startSharing,
    stopSharing,
    retryConnection,
    enableCursor,
    disableCursor,
    toggleCursor,
    onCursorPosition,
  };
  
  return (
    <SupportSessionContext.Provider value={value}>
      {children}
    </SupportSessionContext.Provider>
  );
};

export const useSupportSession = () => {
  const context = useContext(SupportSessionContext);
  if (!context) {
    throw new Error('useSupportSession must be used within a SupportSessionProvider');
  }
  return context;
};

export default SupportSessionContext;
